import {
  endOfMonth,
  endOfQuarter,
  endOfWeek,
  endOfYear,
  format,
  isValid,
  startOfMonth,
  startOfQuarter,
  startOfWeek,
  startOfYear,
} from "date-fns";

/** Defaults when query params are absent (clean URLs). */
export const DEFAULT_APPROVAL_PERIOD = "month";
export const DEFAULT_APPROVAL_VIEW = "pending";

export const APPROVAL_PERIODS = ["day", "week", "month", "quarter", "year", "custom"];
export const APPROVAL_VIEWS = ["pending", "not_approved", "approved", "all"];

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const APPROVAL_PARAM_KEYS = ["employee", "date", "view", "period", "to"];

/**
 * Parse yyyy-MM-dd as local noon to avoid UTC-midnight shifting the calendar day.
 * @param {string | null | undefined} raw
 * @returns {Date | null}
 */
export function parseLocalIsoDate(raw) {
  if (!raw || !ISO_DATE.test(String(raw))) return null;
  const d = new Date(`${raw}T12:00:00`);
  return isValid(d) ? d : null;
}

export function toIsoDate(d) {
  return format(d, "yyyy-MM-dd");
}

export function defaultApprovalDate(now = new Date()) {
  return toIsoDate(startOfMonth(now));
}

/**
 * @param {string | null | undefined} raw
 * @returns {number | null}
 */
export function parseEmployeeId(raw) {
  if (raw == null || raw === "") return null;
  if (!/^\d+$/.test(String(raw))) return null;
  const n = Number(raw);
  if (!Number.isSafeInteger(n) || n <= 0) return null;
  return n;
}

/**
 * @param {string | null | undefined} raw
 */
export function parseApprovalPeriod(raw) {
  const v = String(raw || "").toLowerCase();
  return APPROVAL_PERIODS.includes(v) ? v : DEFAULT_APPROVAL_PERIOD;
}

/**
 * @param {string | null | undefined} raw
 */
export function parseApprovalView(raw) {
  const v = String(raw || "").toLowerCase();
  return APPROVAL_VIEWS.includes(v) ? v : DEFAULT_APPROVAL_VIEW;
}

/**
 * Reconstruct the API date range from period + anchor date.
 * Custom ranges need `to`; otherwise `to` is derived.
 *
 * @param {string} period
 * @param {string} dateIso
 * @param {string | null} [toIso]
 */
export function rangeForPeriod(period, dateIso, toIso = null) {
  const kind = parseApprovalPeriod(period);
  const anchor = parseLocalIsoDate(dateIso) || new Date();

  switch (kind) {
    case "day":
      return { dateFrom: toIsoDate(anchor), dateTo: toIsoDate(anchor) };
    case "week": {
      const from = startOfWeek(anchor, { weekStartsOn: 1 });
      const to = endOfWeek(anchor, { weekStartsOn: 1 });
      return { dateFrom: toIsoDate(from), dateTo: toIsoDate(to) };
    }
    case "quarter": {
      const from = startOfQuarter(anchor);
      const to = endOfQuarter(anchor);
      return { dateFrom: toIsoDate(from), dateTo: toIsoDate(to) };
    }
    case "year": {
      const from = startOfYear(anchor);
      const to = endOfYear(anchor);
      return { dateFrom: toIsoDate(from), dateTo: toIsoDate(to) };
    }
    case "custom": {
      const start = parseLocalIsoDate(dateIso) || anchor;
      const end = parseLocalIsoDate(toIso) || start;
      const [a, b] = start <= end ? [start, end] : [end, start];
      return { dateFrom: toIsoDate(a), dateTo: toIsoDate(b) };
    }
    default: {
      const from = startOfMonth(anchor);
      const to = endOfMonth(anchor);
      return { dateFrom: toIsoDate(from), dateTo: toIsoDate(to) };
    }
  }
}

/**
 * @param {URLSearchParams} sp
 * @param {Date} [now]
 */
export function parseHoursApprovalSearchParams(sp, now = new Date()) {
  const period = parseApprovalPeriod(sp.get("period"));
  const view = parseApprovalView(sp.get("view"));
  const employeeId = parseEmployeeId(sp.get("employee"));
  const date = parseLocalIsoDate(sp.get("date"))
    ? String(sp.get("date"))
    : defaultApprovalDate(now);
  const toRaw = parseLocalIsoDate(sp.get("to")) ? String(sp.get("to")) : null;
  const { dateFrom, dateTo } = rangeForPeriod(period, date, toRaw);

  return { employeeId, date, period, view, dateFrom, dateTo };
}

/**
 * Merge approval filter keys without dropping unrelated query params.
 * Defaults (all employees, current month, pending) are omitted for clean URLs.
 *
 * @param {URLSearchParams} prev
 * @param {{ employee?: number | null; date?: string | null; view?: string | null; period?: string | null; to?: string | null }} patch
 * @param {Date} [now]
 */
export function mergeHoursApprovalSearchParams(prev, patch, now = new Date()) {
  const current = parseHoursApprovalSearchParams(prev, now);

  const employee =
    patch.employee !== undefined ? parseEmployeeId(patch.employee) : current.employeeId;
  const view =
    patch.view !== undefined ? parseApprovalView(patch.view) : current.view;
  const period =
    patch.period !== undefined ? parseApprovalPeriod(patch.period) : current.period;
  const date =
    patch.date !== undefined
      ? parseLocalIsoDate(patch.date)
        ? String(patch.date)
        : defaultApprovalDate(now)
      : current.date;
  const to =
    patch.to !== undefined
      ? parseLocalIsoDate(patch.to)
        ? String(patch.to)
        : null
      : period === "custom"
        ? current.dateTo
        : null;

  const next = new URLSearchParams(prev);
  APPROVAL_PARAM_KEYS.forEach((key) => next.delete(key));

  if (employee != null) next.set("employee", String(employee));
  if (view !== DEFAULT_APPROVAL_VIEW) next.set("view", view);
  if (period !== DEFAULT_APPROVAL_PERIOD) next.set("period", period);

  const isDefaultMonth =
    period === DEFAULT_APPROVAL_PERIOD && date === defaultApprovalDate(now);
  if (!isDefaultMonth) next.set("date", date);

  if (period === "custom") {
    const { dateTo } = rangeForPeriod("custom", date, to);
    next.set("date", date);
    next.set("period", "custom");
    next.set("to", dateTo);
  }

  return next;
}

/**
 * Drop `employee` from the URL only when the id is unknown or out of scope.
 * `fetchUser` returns the axios response on success and the axios error on failure
 * (it does not throw). Network / 401 / 5xx must keep the shared link intact.
 *
 * @param {object | null | undefined} res
 * @returns {boolean}
 */
export function shouldDropEmployeeFromUserFetch(res) {
  const status = res?.response?.status ?? res?.status;
  if (status === 404 || status === 403) return true;
  if (typeof status === "number" && status >= 200 && status < 300) {
    const user = userFromFetchResponse(res);
    return user?.id == null;
  }
  return false;
}

/**
 * @param {object | null | undefined} res
 */
export function userFromFetchResponse(res) {
  return res?.data?.data || res?.data?.user || res?.data;
}
